import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import {
	Button,
	Popover,
	PopoverBody,
} from 'reactstrap';

import { IconButton } from '../../../_shared';
import { messages } from '../../../_shared/i18n/messages';

import styles from './Input.module.scss';

const propTypes = {

	/**
	 * The input id. If none is provided, the input name is used.
	 */
	id: PropTypes.string,

	/**
	 * The input type.
	 */
	type: PropTypes.string.isRequired,

	/**
	 * The input name.
	 */
	name: PropTypes.string.isRequired,

	/**
	 * The input label.
	 */
	label: (props, propName, componentName) => {
		// A visible label is required unless the caller supplies an
		// accessible name another way (ariaLabel) — e.g. inline table-cell
		// controls whose column header is the visual label. (prop-types
		// validators can't be invoked directly from a custom validator, so
		// the accepted shapes — string / element / array / object — are
		// checked inline.)
		const value = props[propName];

		if (value == null) {
			return props.ariaLabel
				? null
				: new Error(`The prop \`${propName}\` is required in \`${componentName}\` unless \`ariaLabel\` is provided.`);
		}

		const isValid = typeof value === 'string'
			|| Array.isArray(value)
			|| (typeof value === 'object' && value !== null);

		return isValid
			? null
			: new Error(`Invalid prop \`${propName}\` supplied to \`${componentName}\`: expected a string, element, array or object.`);
	},

	/**
	 * The input value.
	 */
	value: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.number,
	]),

	/**
	 * The input placeholder text.
	 */
	placeholder: PropTypes.string,

	/**
	 * The id of the feedback text
	 */
	feedbackID: PropTypes.string,

	/**
	 * True, if editing the input value.
	 */
	isEditing: PropTypes.bool,

	/**
	 * True, if the input value is invalid.
	 */
	isInvalid: PropTypes.bool,

	/**
	 * The input aria-label (if any).
	 */
	ariaLabel: PropTypes.string,

	/**
	 * The autocomplete value of the input
	 */
	autocomplete: PropTypes.string,

	/**
	 * True, if the input value is disabled.
	 */
	disabled: PropTypes.bool,

	/**
	 * If true, shows a button to copy the text for text-based
	 * form fields when in display mode.
	 */
	isCopyable: PropTypes.bool,

	/**
	 * Callback function to handle changes to the input value.
	 */
	onChange: PropTypes.func,

};

const Input = (props) => {

	const {
		type = 'text',
		id,
		name,
		label,
		value,
		placeholder,
		feedbackID,
		ariaLabel,
		autocomplete,
		isEditing,
		isInvalid,
		disabled,
		isCopyable,
		onChange,
	} = props;

	const intl = useIntl();

	const textRef = useRef(null);
	const elementId = id || name;

	// Keep track of the visibility state of long text responses, i.e. cut off overflow by default
	const [overflowed, setOverflowed] = useState(false);
	const [expanded, setExpanded] = useState(false);

	useEffect(() => {

		// Check if the text overflowed and needs a "show more" button!
		if (!isEditing && textRef && textRef.current.offsetHeight < textRef.current.scrollHeight) {
			setOverflowed(true);
		}

	}, [isEditing]);

	const [isLinkCopied, setIsLinkCopied] = useState(false);

	const linkPopoverTriggerID = `tc-input-popover-trigger-${elementId}`;
	const linkPopoverContentID = `tc-input-popover-content-${elementId}`;

	const handleCopyLink = () => {

		try {

			const externalLinkInput = document.getElementById(elementId);

			navigator.clipboard.writeText(externalLinkInput.innerText);

			// Close the alert after 2 seconds
			setTimeout(() => setIsLinkCopied(false), 2000);

		// If copy fails or is not supported, let user copy the link themselves
		} catch (error) {
			return;
		}

	};

	if (!isEditing) {

		return (
			<>
				<div className="d-flex align-items-center">
					<div
						ref={textRef}
						className={`${styles['tc-material-input-text-display']} ${expanded ? '' : styles['show-less']}`}>
						{value &&
							<span id={elementId}>{value}</span>
						}
						{!value &&
							<span className="tc-material-input-display-empty-value">
								<FormattedMessage {...messages.TC_COMPONENTS_FORM_INPUT_TEXT_EMPTY_VALUE} />
							</span>
						}
					</div>
					{isCopyable && value &&
						<>
							<IconButton
								id={linkPopoverTriggerID}
								aria-expanded={isLinkCopied}
								title={intl.formatMessage(messages.TC_COMPONENTS_GENERAL_TEXT_COPY)}
								className="ms-2"
								size="1.5rem"
								icon="far fa-copy"
								onClick={handleCopyLink} />
							<Popover
								id={linkPopoverContentID}
								popperClassName="info-tooltip"
								target={linkPopoverTriggerID}
								trigger="click"
								placement="top"
								isOpen={isLinkCopied}
								toggle={() => setIsLinkCopied((prev) => !prev)}>
								<PopoverBody>
									<FormattedMessage {...messages.TC_COMPONENTS_GENERAL_TEXT_COPY_SUCCESS} />
								</PopoverBody>
							</Popover>
						</>
					}
				</div>
				{overflowed && !expanded &&
					<Button
						color="link"
						className="pl-0"
						onClick={() => setExpanded(true)}>
						<FormattedMessage {...messages.TC_COMPONENTS_GENERAL_TEXT_SHOW_MORE} />
					</Button>
				}
			</>
		);

	}

	return (
		<input
			id={elementId}
			className={`tc-material-input ${isInvalid ? 'tc-material-input-invalid' : ''}`}
			aria-describedby={isInvalid ? feedbackID : undefined}
			autoComplete={autocomplete}
			type={type}
			name={name}
			aria-label={ariaLabel || label}
			value={value || ''}
			placeholder={placeholder}
			disabled={disabled}
			onChange={(event) => onChange(event.target.value)} />
	);

};

Input.propTypes = propTypes;

export default Input;
